/* === AgPopover — Anagogia Design System === */

import { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useCalqueEchap } from '../utils/useCalqueEchap';
import { usePortalContainer } from '../utils/usePortalContainer';
import styles from './AgPopover.module.css';

interface AgPopoverProps {
  /** Element the popover is anchored to */
  anchorEl: HTMLElement | null;
  /** Whether the popover is open */
  open: boolean;
  /** Called when the popover requests to close */
  onClose: () => void;
  /** Anchor origin point */
  anchorOrigin?: {
    vertical: 'top' | 'center' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
  };
  /** Transform origin point */
  transformOrigin?: {
    vertical: 'top' | 'center' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
  };
  /** Content */
  children: React.ReactNode;
  /** Additional class name */
  className?: string;
  /** Inline styles */
  style?: React.CSSProperties;
}

interface PopoverPosition {
  top: number;
  left: number;
}

function getOriginPoint(
  rect: DOMRect,
  origin: { vertical: 'top' | 'center' | 'bottom'; horizontal: 'left' | 'center' | 'right' },
): { x: number; y: number } {
  let x: number;
  let y: number;

  switch (origin.horizontal) {
    case 'left':
      x = rect.left;
      break;
    case 'center':
      x = rect.left + rect.width / 2;
      break;
    case 'right':
      x = rect.right;
      break;
  }

  switch (origin.vertical) {
    case 'top':
      y = rect.top;
      break;
    case 'center':
      y = rect.top + rect.height / 2;
      break;
    case 'bottom':
      y = rect.bottom;
      break;
  }

  return { x, y };
}

function getTransformOffset(
  popoverRect: DOMRect,
  origin: { vertical: 'top' | 'center' | 'bottom'; horizontal: 'left' | 'center' | 'right' },
): { x: number; y: number } {
  let x: number;
  let y: number;

  switch (origin.horizontal) {
    case 'left':
      x = 0;
      break;
    case 'center':
      x = popoverRect.width / 2;
      break;
    case 'right':
      x = popoverRect.width;
      break;
  }

  switch (origin.vertical) {
    case 'top':
      y = 0;
      break;
    case 'center':
      y = popoverRect.height / 2;
      break;
    case 'bottom':
      y = popoverRect.height;
      break;
  }

  return { x, y };
}

// Constantes de module : des littéraux par défaut recréés à chaque rendu relanceraient
// `updatePosition` (et son `setPosition`) sans fin quand l'appelant n'en passe pas.
const DEFAULT_ANCHOR_ORIGIN: NonNullable<AgPopoverProps['anchorOrigin']> = { vertical: 'bottom', horizontal: 'left' };
const DEFAULT_TRANSFORM_ORIGIN: NonNullable<AgPopoverProps['transformOrigin']> = { vertical: 'top', horizontal: 'left' };

function AgPopoverPortal({
  anchorEl,
  onClose,
  anchorOrigin = DEFAULT_ANCHOR_ORIGIN,
  transformOrigin = DEFAULT_TRANSFORM_ORIGIN,
  children,
  className,
  style,
}: Omit<AgPopoverProps, 'open'>) {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<PopoverPosition>({ top: 0, left: 0 });

  const updatePosition = useCallback(() => {
    if (!anchorEl || !popoverRef.current) return;

    const anchorRect = anchorEl.getBoundingClientRect();
    const popoverRect = popoverRef.current.getBoundingClientRect();

    const anchor = getOriginPoint(anchorRect, anchorOrigin);
    const offset = getTransformOffset(popoverRect, transformOrigin);

    let top = anchor.y - offset.y;
    let left = anchor.x - offset.x;

    // Clamp to viewport
    const padding = 8;
    if (left + popoverRect.width > window.innerWidth - padding) {
      left = window.innerWidth - popoverRect.width - padding;
    }
    if (left < padding) left = padding;

    if (top + popoverRect.height > window.innerHeight - padding) {
      top = window.innerHeight - popoverRect.height - padding;
    }
    if (top < padding) top = padding;

    setPosition({ top, left });
  }, [anchorEl, anchorOrigin, transformOrigin]);

  useEffect(() => {
    updatePosition();
  }, [updatePosition]);

  // Échap : pile commune des calques (le popover ne ferme pas la fenêtre dessous)
  useCalqueEchap(true, onClose);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        popoverRef.current &&
        !popoverRef.current.contains(target) &&
        anchorEl &&
        !anchorEl.contains(target)
      ) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose, anchorEl]);

  return (
    <div
      ref={popoverRef}
      className={[styles.popover, className].filter(Boolean).join(' ')}
      style={{ ...style, top: position.top, left: position.left }}
    >
      {children}
    </div>
  );
}

/**
 * Surface flottante ancrée à un élément déclencheur.
 * @category Overlays
 */
function AgPopover(props: AgPopoverProps) {
  // En plein écran, un portail dans `document.body` n'est jamais peint :
  // la surface suit l'élément plein écran qui contient son ancre.
  const portalContainer = usePortalContainer(props.anchorEl, props.open);

  if (!props.open || !props.anchorEl) return null;

  return createPortal(
    <AgPopoverPortal
      anchorEl={props.anchorEl}
      onClose={props.onClose}
      anchorOrigin={props.anchorOrigin}
      transformOrigin={props.transformOrigin}
      className={props.className}
      style={props.style}
    >
      {props.children}
    </AgPopoverPortal>,
    portalContainer,
  );
}

export { AgPopover };
export type { AgPopoverProps };
export default AgPopover;
